import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AUTH_REQUIRED_EVENT, api, clearLegacyApiToken, type Me } from './lib/api.js';
import { LibraryPage } from './pages/LibraryPage.js';
import { JoinPage } from './pages/JoinPage.js';
import { MeetingPage } from './pages/MeetingPage.js';
import { CalendarPage } from './pages/CalendarPage.js';
import { KeywordsPage } from './pages/KeywordsPage.js';
import { ModelsPage } from './pages/ModelsPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { AccountPage } from './pages/AccountPage.js';
import { DevelopersPage } from './pages/DevelopersPage.js';
import { IntegrationsPage } from './pages/IntegrationsPage.js';
import { BrandWordmark } from './components/BrandMark.js';
import { applyDocumentBrand, currentBrand, documentHeading, type Brand } from './lib/brand.js';
import { DocsPage } from './pages/DocsPage.js';

/**
 * Read one-time auth params (?token= deep link, ?invite= setup link, ?reset= password
 * reset link, ?error=)
 * at startup and strip secrets from the address bar / history immediately.
 */
function takeUrlParams(): {
  token: string | null;
  invite: string | null;
  reset: string | null;
  error: string | null;
} {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('token');
  const invite = url.searchParams.get('invite');
  const reset = url.searchParams.get('reset');
  const error = url.searchParams.get('error');
  if (token || invite || reset || error) {
    url.searchParams.delete('token');
    url.searchParams.delete('invite');
    url.searchParams.delete('reset');
    url.searchParams.delete('error');
    const path = url.pathname === '/login' && !invite && !reset ? '/' : url.pathname;
    window.history.replaceState(null, '', path + (url.search === '?' ? '' : url.search) + url.hash);
  }
  return { token, invite, reset, error };
}

const initialParams = takeUrlParams();

export function App(): JSX.Element {
  const [me, setMe] = useState<Me | null>(null);
  const [authEpoch, setAuthEpoch] = useState(0);
  const [loadingMe, setLoadingMe] = useState(true);
  const [authError, setAuthError] = useState<string | null>(initialParams.error);
  const [inviteToken, setInviteToken] = useState<string | null>(initialParams.invite);
  const [resetToken, setResetToken] = useState<string | null>(initialParams.reset);
  const deepLinkToken = useRef<string | null>(initialParams.token);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    clearLegacyApiToken();
    const onAuth = () => setMe((m) => (m ? { ...m, authenticated: false } : m));
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuth);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuth);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run(): Promise<void> {
      setLoadingMe(true);
      // One-step deep link: /login?token=... signs in once, then is forgotten.
      const t = deepLinkToken.current;
      if (t) {
        deepLinkToken.current = null;
        try {
          await api.tokenLogin(t);
        } catch {
          if (!cancelled) setAuthError('That sign-in link is not valid or has been revoked.');
        }
      }
      try {
        const r = await api.me();
        if (!cancelled) setMe(r);
      } catch {
        if (!cancelled) setMe({ authenticated: false, ssoConfigured: false });
      } finally {
        if (!cancelled) setLoadingMe(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [authEpoch]);

  const authed = !!me?.authenticated;
  const isAdmin = authed && me?.role === 'admin';

  function onSignedIn(): void {
    setAuthError(null);
    setInviteToken(null);
    setResetToken(null);
    navigate('/', { replace: true });
    setAuthEpoch((n) => n + 1);
  }

  async function signOut(): Promise<void> {
    try {
      await api.logout();
    } finally {
      setMe((m) => (m ? { ...m, authenticated: false } : m));
      navigate('/login', { replace: true });
    }
  }

  const displayName = me?.name || me?.email;
  const brand = currentBrand();

  useEffect(() => {
    const page = loadingMe ? '' : documentHeading(location.pathname, location.hash, authed);
    applyDocumentBrand(brand, page);
  }, [brand, location.pathname, location.hash, authed, loadingMe]);

  return (
    <div className="app" data-brand={brand.id}>
      <header className="app-header">
        <div className="app-header__inner">
          <NavLink to="/" className="app-brand" aria-label={`${brand.name} home`}>
            <BrandWordmark size="header" className="app-brand__wordmark" />
          </NavLink>
          {authed ? <MainNav brand={brand} isAdmin={isAdmin} /> : <span />}
          <div className="app-nav app-nav--user">
            {authed && me && (
              <>
                {brand.id === 'agentmeetings' && (
                  <span className="live-chip">
                    <span className="live-chip__dot" aria-hidden="true" />
                    Live API
                  </span>
                )}
                {brand.id === 'agentmeetings' && <span className="app-header__rule" aria-hidden="true" />}
                <NavLink to="/settings/account" className="app-user" title={me.name || me.email}>
                  <span className="app-user__avatar">{initials(displayName)}</span>
                  <span className="app-user__id">
                    <span className="app-user__email">{me.email}</span>
                    {me.role && <span className="app-user__role">{me.role}</span>}
                  </span>
                </NavLink>
                <button type="button" className="app-signout" onClick={() => void signOut()}>
                  Sign out
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="app-main">
        {loadingMe ? (
          <div className="empty"><span className="spinner" /> Checking session&hellip;</div>
        ) : !authed ? (
          <LoginPage
            key={inviteToken ?? resetToken ?? 'login'}
            onSignedIn={onSignedIn}
            ssoConfigured={!!me?.ssoConfigured}
            initialError={authError}
            inviteToken={inviteToken}
            onInviteDone={() => {
              setInviteToken(null);
              navigate('/login', { replace: true });
            }}
            resetToken={resetToken}
            resetEmailEnabled={!!me?.passwordResetEmail}
            onResetDone={() => {
              setResetToken(null);
              navigate('/login', { replace: true });
            }}
          />
        ) : (
          <Routes key={authEpoch}>
            <Route path="/" element={<LibraryPage />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/meetings/:id" element={<MeetingPage />} />
            <Route path="/settings/calendar" element={<CalendarPage />} />
            <Route path="/settings/keywords" element={<KeywordsPage />} />
            <Route path="/settings/models" element={<ModelsPage />} />
            <Route path="/settings/integrations" element={<IntegrationsPage />} />
            <Route path="/settings/push" element={<Navigate to="/settings/integrations" replace />} />
            <Route path="/settings/developers" element={<DevelopersPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route
              path="/settings/account"
              element={
                <AccountPage
                  email={me!.email!}
                  name={me!.name}
                  role={me!.role}
                  hasPassword={!!me!.hasPassword}
                  onChanged={() => void api.me().then(setMe)}
                />
              }
            />
            {isAdmin && <Route path="/admin" element={<UsersPage currentUserId={me!.userId} />} />}
            {isAdmin && <Route path="/settings/users" element={<Navigate to="/admin" replace />} />}
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </main>
      <footer className="app-footer">
        <div className="app-footer__inner">
          <span>{brand.name} · {brand.tagline}</span>
          <span>{brand.domain}</span>
        </div>
      </footer>
      {!authed && !loadingMe && location.pathname !== '/login' && !inviteToken && !resetToken && <LoginPathSync />}
    </div>
  );
}

/** Keep the address bar at /login while signed out (deep links still work after sign-in via Library). */
function LoginPathSync(): null {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/login', { replace: true });
  }, [navigate]);
  return null;
}

function MainNav({ brand, isAdmin }: { brand: Brand; isAdmin: boolean }): JSX.Element {
  if (brand.id === 'agentmeetings') {
    return (
      <nav className="app-nav app-nav--main" aria-label="Primary">
        <NavLink to="/" end className={linkClass}>Library</NavLink>
        <NavLink to="/join" className={linkClass}>New Meeting</NavLink>
        <NavLink to="/settings/calendar" className={linkClass}>Calendar</NavLink>
        <HashNav hashes={['#agents', '#mcp']}>Agents</HashNav>
        <HashNav hashes={['#api-keys']}>API Keys</HashNav>
        <HashNav hashes={['#webhooks']}>Webhooks</HashNav>
        <HashNav hashes={['#logs']}>Logs</HashNav>
        <NavLink to="/docs" className={linkClass}>Docs</NavLink>
        <NavLink to="/settings/integrations" className={linkClass}>Integrations</NavLink>
        {isAdmin && <NavLink to="/admin" className={linkClass}>Admin</NavLink>}
      </nav>
    );
  }
  return (
    <nav className="app-nav app-nav--main" aria-label="Primary">
      <NavLink to="/" end className={linkClass}>Library</NavLink>
      <NavLink to="/join" className={linkClass}>New Meeting</NavLink>
      <NavLink to="/settings/calendar" className={linkClass}>Calendar</NavLink>
      <NavLink to="/settings/keywords" className={linkClass}>Keywords</NavLink>
      <NavLink to="/settings/models" className={linkClass}>Models</NavLink>
      <NavLink to="/settings/integrations" className={linkClass}>Integrations</NavLink>
      <NavLink to="/settings/developers" className={linkClass}>Developers</NavLink>
      {isAdmin && <NavLink to="/admin" className={linkClass}>Admin</NavLink>}
    </nav>
  );
}

function HashNav({ hashes, children }: { hashes: string[]; children: string }): JSX.Element {
  const { pathname, hash } = useLocation();
  const active = pathname === '/settings/developers' && hashes.includes(hash);
  return (
    <Link
      to={`/settings/developers${hashes[0]}`}
      className={active ? 'app-nav__link app-nav__link--active' : 'app-nav__link'}
      aria-current={active ? 'page' : undefined}
    >
      {children}
    </Link>
  );
}

function linkClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'app-nav__link app-nav__link--active' : 'app-nav__link';
}

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const base = name.includes('@') ? name.split('@')[0]! : name;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  const out = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : base.slice(0, 2);
  return out.toUpperCase();
}

function NotFound(): JSX.Element {
  return (
    <div className="empty">
      <h2>Not found</h2>
      <p>The page you&rsquo;re looking for doesn&rsquo;t exist.</p>
    </div>
  );
}
